/**
 * Shared utilities used by both admin (app.js) and MVP (mvp.js) render modules.
 */

/** Escape HTML entities */
export function esc(str) {
  const el = document.createElement('div');
  el.textContent = str || '';
  return el.innerHTML;
}

/** Format a number, returning em-dash for null/Infinity */
export function fmt(n, decimals = 1) {
  if (n == null || !isFinite(n)) return '\u2014';
  return n.toFixed(decimals);
}

/** Format minutes as human-readable string */
export function fmtMin(min) {
  if (min >= 120) return `${(min / 60).toFixed(1)} hrs`;
  if (min >= 1) return `${Math.round(min)} min`;
  return '<1 min';
}

/** Format minutes as HTML with unit span */
export function fmtTimeHtml(min) {
  if (min >= 120) {
    return `${(min / 60).toFixed(1)}<span class="unit">hrs</span>`;
  }
  if (min >= 1) {
    return `${Math.round(min)}<span class="unit">min</span>`;
  }
  return `&lt;1<span class="unit">min</span>`;
}

/** Format large numbers compactly */
export function fmtCompact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}<span class="unit">M</span>`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}<span class="unit">K</span>`;
  return n.toLocaleString();
}

/** Calculate median of a sorted array */
export function medianOf(arr) {
  if (arr.length === 0) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

/** Animate a counter from 0 to target */
export function animateCounter(el, target, duration = 1800) {
  const start = performance.now();
  const decimals = target >= 100 ? 0 : 1;

  const update = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = target * eased;
    el.textContent = current.toFixed(decimals);
    if (progress < 1) {
      requestAnimationFrame(update);
    } else {
      el.textContent = target.toFixed(decimals);
    }
  };

  requestAnimationFrame(update);
}

/**
 * Build a Map<gizmoId, label> mapping hex IDs to "Custom GPT #N".
 * Numbered by first appearance order across conversations sorted by create_time.
 */
export function buildGizmoLabels(conversations) {
  const labels = new Map();
  let n = 0;
  const sorted = [...conversations].sort((a, b) => (a.createTime || 0) - (b.createTime || 0));
  for (const c of sorted) {
    const id = c.gizmoId;
    if (id && !labels.has(id)) {
      n++;
      labels.set(id, `Custom GPT #${n}`);
    }
  }
  return labels;
}

/**
 * Get display label for a gizmo ID, falling back to truncated hex.
 */
export function gizmoLabel(id, labels) {
  if (!id) return '\u2014';
  return labels.get(id) || id.slice(0, 8) + '...';
}
